import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from './use-toast';
import { Plan } from '@/lib/plans/types';

interface SyncPlanData {
  external_id: string;
  system_code: string;
  name: string;
  description?: string;
  price_monthly: number;
  price_yearly: number;
  features: {
    limits: Record<string, number | undefined>;
    toggles: Record<string, boolean | undefined>;
  };
  is_active: boolean;
  sort_order: number;
}

interface SyncPlanResponse {
  success: boolean;
  data: {
    plan_id: string;
    is_new: boolean;
  };
  error?: {
    code: string;
    message: string;
  };
}

interface PlanWithSync extends Plan {
  console_plan_id?: string | null;
  console_synced_at?: string | null;
}

export function useConsolePlans() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Always configured - credentials are on the server
  const isConfigured = true;

  const syncPlan = async (plan: PlanWithSync, systemCode: string): Promise<SyncPlanResponse['data'] | null> => {
    const planData: SyncPlanData = {
      external_id: plan.id,
      system_code: systemCode.toLowerCase(),
      name: plan.name,
      description: plan.description,
      price_monthly: Math.round(plan.price_monthly * 100), // Converter para centavos
      price_yearly: Math.round(plan.price_yearly * 100), // Converter para centavos
      features: {
        limits: plan.features?.limits || {},
        toggles: plan.features?.toggles || {},
      },
      is_active: plan.is_active,
      sort_order: plan.sort_order,
    };

    const { data, error: invokeError } = await supabase.functions.invoke('console-sync-plan', {
      body: planData,
    });

    if (invokeError) {
      throw new Error(invokeError.message || 'Erro ao chamar função de sincronização');
    }

    const response = data as SyncPlanResponse;

    if (!response.success) {
      throw new Error(response.error?.message || 'Erro ao sincronizar plano');
    }

    return response.data;
  };

  const syncSinglePlan = async (plan: PlanWithSync, systemCode: string) => {
    setLoading(true);
    setError(null);

    try {
      const result = await syncPlan(plan, systemCode);
      
      // Invalidar cache para atualizar a lista
      queryClient.invalidateQueries({ queryKey: ['system-plans'] });
      
      toast({
        title: 'Plano sincronizado!',
        description: `${plan.name} foi sincronizado com a Console.`,
      });
      
      return result;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Erro na sincronização',
        description: err.message,
        variant: 'destructive',
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const syncAllPlans = async (plans: PlanWithSync[], systemCode: string) => {
    setLoading(true);
    setError(null);
    setProgress({ current: 0, total: plans.length });

    const results: { success: PlanWithSync[]; failed: { plan: PlanWithSync; error: string }[] } = {
      success: [],
      failed: [],
    };

    try {
      for (let i = 0; i < plans.length; i++) {
        const plan = plans[i];
        setProgress({ current: i + 1, total: plans.length });

        try {
          await syncPlan(plan, systemCode);
          results.success.push(plan);
        } catch (err: any) {
          results.failed.push({ plan, error: err.message });
        }
      }

      // Invalidar cache para atualizar a lista
      queryClient.invalidateQueries({ queryKey: ['system-plans'] });

      if (results.failed.length === 0) {
        toast({
          title: 'Sincronização concluída!',
          description: `${results.success.length} plano(s) sincronizado(s) com sucesso.`,
        });
      } else if (results.success.length > 0) {
        toast({
          title: 'Sincronização parcial',
          description: `${results.success.length} sucesso, ${results.failed.length} falha(s).`,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Falha na sincronização',
          description: `Todos os ${results.failed.length} planos falharam.`,
          variant: 'destructive',
        });
      }

      return results;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  return {
    loading,
    error,
    progress,
    isConfigured,
    syncSinglePlan,
    syncAllPlans,
  };
}
